// todo ask what options should be in Date dropdown
import type { FC } from 'react'
import React, { useMemo, useState, useCallback } from 'react'
import { makeStyles, createStyles, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useDispatch, useSelector } from 'react-redux'
import clsx from 'clsx'
import Grid from '@material-ui/core/Grid'
import { Drawer } from '@material-ui/core'

import {
  Typography,
  Select,
  Button,
  IconButton,
  Pagination,
  Table,
  Filters,
  FloatingButton,
  WindowTitle,
} from '@homeviews/shared-ui'
import {
  setDevelopmentsFilter,
  selectDevelopmentsFilters,
  clearDevelopmentsFilters,
  useGetDevelopmentsQuery,
  useGetDevelopmentsFiltersQuery,
  getUser,
} from '@homeviews/store'
import type { FilterType, Development } from '@homeviews/models'
import type { ITheme } from '@homeviews/styles'
import {
  createRows,
  headCells,
  DevelopmentSkeletonCard,
  createSkeletonRows,
} from '@homeviews/developments'
import { mixpanelTrack } from '@homeviews/seo'
import { transformFilterOptions } from '@homeviews/utils'
import { DevelopmentCard, RATING_OPTIONS } from '@homeviews/developments'

import { HAS_PERMISSION } from '@homeviews/users'

const useStyles = makeStyles((theme: ITheme) =>
  createStyles({
    root: {
      padding: theme.spacing(
        theme.layout.contentHorizontalPadding,
        theme.layout.contentVerticalPadding,
      ),
      '& .h-editDevDisabled': {
        display: 'none',
      },
    },
    reviewCardRoot: {
      marginBottom: theme.spacing(2.5),
    },
    selectWrapper: {
      width: '250px',
      fontWeight: 500,
      marginRight: theme.spacing(0.75),
      marginBottom: theme.spacing(1.875 / 2),
      marginTop: theme.spacing(1.5 / 2),
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    selectWrapperLast: {
      marginRight: 'auto',
    },
    filtersTitle: {
      color: theme.colors.grey600,
      fontWeight: 600,
      marginRight: 8,
    },
    filtersSummary: {
      color: theme.colors.grey600,
      fontWeight: 500,
      marginTop: theme.spacing(1.25),
      fontSize: '16px',
      [theme.breakpoints.down('sm')]: {
        marginBottom: 20,
      },
    },
    paginationWrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      marginTop: theme.spacing(2.5),
    },
    paginationTopWrapper: {
      marginTop: 0,
    },
    viewButtons: {
      display: 'flex',
      marginLeft: 10,
    },
    toggleViewButton: {
      opacity: 0.5,
    },
    toggleViewButtonActive: {
      opacity: 1,
    },
    mobileFiltersHeader: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: theme.spacing(1),
      '& h3': {
        marginRight: 'auto',
      },
    },
    mobileFiltersDrawer: {
      '& .MuiDrawer-paper': {
        padding: theme.spacing(3.75),
        overflow: 'initial',
      },
    },
    clickDisabled: {
      pointerEvents: 'none',
      '& .c-developmentCard__editButton': {
        display: 'none',
      },
    },
  }),
)

const skeletonRows = createSkeletonRows()

const Developments: FC = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const theme = useTheme()
  const activeFilters = useSelector(selectDevelopmentsFilters)
  const user = useSelector(getUser)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [mobileFiltersOpened, setMobileFiltersOpened] = useState<boolean>(false)

  const [listView, setListView] = useState<'cards' | 'table'>('cards')

  const { data: response, isFetching } = useGetDevelopmentsQuery(activeFilters)
  const { data: locationOptionsResponse } =
    useGetDevelopmentsFiltersQuery('location')

  const devOptions = useMemo(
    () => transformFilterOptions(locationOptionsResponse),
    [locationOptionsResponse],
  )

  const developments = response?.data || []
  const meta = response?.meta

  const handleSelectFilterChange = (filterType: FilterType) => (option) => {
    dispatch(
      setDevelopmentsFilter({
        filter: filterType,
        value: option?.value || false,
      }),
    )

    if (filterType === 'location') {
      mixpanelTrack('DEVELOPMENTS_LOCATION_FILTER', {
        value: option?.label,
      })
    } else if (filterType === 'rating') {
      mixpanelTrack('DEVELOPMENTS_RATING_FILTER', {
        value: option?.label,
      })
    }
  }

  const handlePerPageChange = (page: number) => {
    dispatch(
      setDevelopmentsFilter({
        filter: 'posts_per_page',
        value: page,
      }),
    )
  }

  const handlePageChange = (page: number) => {
    dispatch(
      setDevelopmentsFilter({
        filter: 'page',
        value: page,
      }),
    )
  }

  const handleClearFilters = () => {
    dispatch(clearDevelopmentsFilters())
    mixpanelTrack('DEVELOPMENTS_RESET_FILTERS')
  }

  const isEditable = useCallback(
    (id: string) => {
      return HAS_PERMISSION(user, 'EDIT_DEVELOPMENT', 'development', {
        devId: id,
      })
    },
    [user],
  )

  const rows = useMemo(
    () => (listView === 'cards' ? [] : createRows(developments, isEditable)),
    [developments, listView, isEditable],
  )

  const defaultPerPage = 20

  const renderSelectDropdowns = () => (
    <>
      <div className={classes.selectWrapper}>
        <Select
          placeholder="Select Location"
          options={devOptions}
          onChange={handleSelectFilterChange('location')}
          value={activeFilters?.['location']}
          isClearable
        />
      </div>
      <div className={classes.selectWrapper}>
        <Select
          placeholder="Select Rating"
          options={RATING_OPTIONS}
          onChange={handleSelectFilterChange('rating')}
          value={activeFilters?.['rating']}
          isClearable
        />
      </div>
    </>
  )

  const checkDevelopmentEditPermissions = (development: Development) => {
    if (!development.is_editable) {
      return classes.clickDisabled
    }

    if (
      !HAS_PERMISSION(user, 'EDIT_DEVELOPMENT', 'development', {
        devId: development.id,
      })
    ) {
      return classes.clickDisabled
    }
  }

  return (
    <>
      <WindowTitle title="Developments" />
      <div className={classes.root}>
        <Typography variant="h1">Developments</Typography>
        {!!meta?.total && !!developments?.length && (
          <Typography
            className={classes.filtersSummary}
          >{`${developments.length} shown of ${meta.total}`}</Typography>
        )}
        {!isMobile && (
          <Filters>
            <Typography className={classes.filtersTitle}>Filters</Typography>
            {renderSelectDropdowns()}
            <div
              className={clsx(classes.selectWrapper, classes.selectWrapperLast)}
            >
              <Button
                variant="outlined"
                customColor="dark"
                onClick={handleClearFilters}
              >
                Reset filters
              </Button>
            </div>

            <div
              className={clsx(
                classes.paginationWrapper,
                classes.paginationTopWrapper,
              )}
            >
              <Pagination
                currentPage={meta?.current_page}
                lastPage={meta?.last_page}
                perPage={activeFilters?.['posts_per_page'] || defaultPerPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
              />
              <div className={classes.viewButtons}>
                <IconButton
                  svgIcon="medium/grid"
                  svgSource="image"
                  variant="transparent"
                  onClick={() => {
                    setListView('cards')
                    mixpanelTrack('DEVELOPMENTS_SELECT_CARD_VIEW')
                  }}
                  className={clsx(
                    classes.toggleViewButton,
                    listView === 'cards' && classes.toggleViewButtonActive,
                  )}
                />
                <IconButton
                  svgIcon="medium/list"
                  svgSource="image"
                  variant="transparent"
                  onClick={() => {
                    setListView('table')
                    mixpanelTrack('DEVELOPMENTS_SELECT_TABLE_VIEW')
                  }}
                  className={clsx(
                    classes.toggleViewButton,
                    listView === 'table' && classes.toggleViewButtonActive,
                  )}
                />
              </div>
            </div>
          </Filters>
        )}
        {isFetching && listView === 'cards' && (
          <Grid container spacing={3}>
            {Array(12)
              .fill('0')
              .map((i, index) => (
                <Grid key={index} item xs={12} sm={6} md={4} lg={3} xl={2}>
                  <DevelopmentSkeletonCard />
                </Grid>
              ))}
          </Grid>
        )}
        {!isFetching && !!developments?.length && listView === 'cards' && (
          // todo fix type, 'name' property destructing bug
          <Grid container spacing={3}>
            {developments.map((development: Development) => {
              const {
                id,
                name,
                rating,
                images,
                town,
                ranked_out_of,
                current_ranking,
                change_in_rank,
                no_of_reviews,
                developers,
                is_editable,
              } = development
              return (
                <Grid key={id} item xs={12} sm={6} md={4} lg={3} xl={2}>
                  <DevelopmentCard
                    id={id}
                    name={name}
                    rating={rating}
                    imageSrc={images[0]?.url}
                    area={town}
                    currentRanking={current_ranking}
                    rankedOutOf={ranked_out_of}
                    developers={developers}
                    changeInRanking={change_in_rank}
                    noReviews={no_of_reviews}
                    className={checkDevelopmentEditPermissions(development)}
                    isEditable={is_editable}
                  />
                </Grid>
              )
            })}
          </Grid>
        )}
        {listView === 'table' && (
          <Table
            headCells={headCells}
            rows={isFetching ? skeletonRows : rows}
            activeSort={null}
          />
        )}
        {!developments?.length && !isFetching && (
          <div className="l-noRecords">No Developments</div>
        )}
        <div className={classes.paginationWrapper}>
          <Pagination
            currentPage={meta?.current_page}
            lastPage={meta?.last_page}
            perPage={activeFilters?.['posts_per_page'] || defaultPerPage}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
          />
        </div>
        {mobileFiltersOpened && (
          <Drawer
            open={true}
            anchor="bottom"
            onClose={() => setMobileFiltersOpened(false)}
            className={classes.mobileFiltersDrawer}
          >
            <div className={classes.mobileFiltersHeader}>
              <Typography variant="h3">Filters</Typography>
              <Button
                variant="outlined"
                customColor="dark"
                onClick={handleClearFilters}
              >
                Reset filters
              </Button>
            </div>
            {renderSelectDropdowns()}
          </Drawer>
        )}

        {isMobile && (
          <FloatingButton
            svgIcon="filters"
            variant="extended"
            color="primary"
            onClick={() => setMobileFiltersOpened(true)}
          >
            Filters
          </FloatingButton>
        )}
      </div>
    </>
  )
}

export default Developments
